import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useEntityPermission,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import ListBoxSelector from '@product-framework/components/ListBoxSelector';
import { UnPaginatedList } from '@product-framework/index';

function IndustriesServiced({
  onChange = () => undefined,
  serviceProviderId,
  permissions,
}) {
  const leftNameKey = 'industryName';
  const leftValueKey = 'id';
  const rightValueKey = 'id';
  const limit = 10;

  const { isReadOnly } = useEntityPermission();
  const readOnly = isReadOnly(permissions?.industryId, '');

  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, post, remove, getApiUrl } = useDataProvider(provider);

  const rightTitle = formatMessage({
    id: 'SERVICE_PROVIDER_SKILLS_INDUSTRIES_SERVICED_HEADING',
    defaultMessage: 'Industries Serviced',
  });

  const getLeftItems = async page => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/industries?page=${page}&limit=${limit}`,
    );
    if (response.ok) {
      const { data = [], ...other } = response.data;
      const resultsData =
        data?.map(item => ({
          label: item[leftNameKey],
          value: item[leftValueKey],
        })) || [];

      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return [];
  };

  const getRightItems = async page => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/service-industries?page=${page}&limit=${limit}`,
    );
    if (response.ok) {
      const { data = [], ...other } = response.data;
      const resultsData =
        data?.map(item => ({
          label: item.industryName,
          value: item,
        })) || [];
      onChange(resultsData);
      return { data: resultsData, ...other };
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return { data: [] };
  };

  const onItemsAdded = async items => {
    let successItems = 0;
    if (Array.isArray(items)) {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        const response = await post(
          `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/service-industries`,
          {
            industryId: item,
            locationId: null,
          },
        );
        if (!response.ok) {
          snackbar.showMessage({
            type: 'error',
            data: <SnackbarApiErrorMessage errors={response?.errors} />,
          });
        } else {
          successItems += 1;
        }
      }
      if (successItems === items.length) {
        snackbar.showMessage(
          formatMessage({
            id: 'SERVICEPROVIDER_SKILLS_INDUSTRIES_SAVE_SUCCESS',
            defaultMessage:
              'Service Provider industries were saved successfully',
          }),
        );
      }
    }
  };

  const onItemsRemoved = async items => {
    let successItems = 0;
    if (Array.isArray(items)) {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        const response = await remove(
          `${getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/service-industries/${
            item[rightValueKey]
          }`,
        );
        if (!response.ok) {
          snackbar.showMessage({
            type: 'error',
            data: <SnackbarApiErrorMessage errors={response?.errors} />,
          });
        } else {
          successItems += 1;
        }
      }
      if (successItems === items.length) {
        snackbar.showMessage(
          formatMessage({
            id: 'SERVICEPROVIDER_SKILLS_INDUSTRIES_REMOVE_SUCCESS',
            defaultMessage:
              'Service Provider industries were removed successfully',
          }),
        );
      }
    }
  };

  if (readOnly) {
    return <UnPaginatedList title={rightTitle} getItems={getRightItems} />;
  }

  return (
    <ListBoxSelector
      height="390px"
      getLeftItems={getLeftItems}
      getRightItems={getRightItems}
      parentId={null}
      onChange={onChange}
      onItemsAdded={onItemsAdded}
      onItemsRemoved={onItemsRemoved}
      leftTitle={formatMessage({
        id: 'SERVICE_PROVIDER_SKILLS_INDUSTRIES_SERVICED_HEADING',
        defaultMessage: 'Industries List',
      })}
      rightTitle={rightTitle}
    />
  );
}

export default IndustriesServiced;
